import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  ArrowBack,
  Refresh,
  ReplayOutlined,
  ErrorOutline,
  CheckCircleOutline,
  HourglassEmptyOutlined,
  ListAltOutlined,
  Fullscreen,
  FullscreenExit,
} from "@material-ui/icons";
import fetchClient from "../../api/fetchClient";
import message from "../../helper/message";

const OBJECT_TYPE_LABELS = {
  "4":  "Item",
  "67": "Transferência de Estoque",
};

const OBJECT_TYPE_OPTIONS = [
  { value: "",   label: "Todos" },
  { value: "4",  label: "4 - Item" },
  { value: "67", label: "67 - Transferência de Estoque" },
];

// Mapeamento ObjectType → chave do scheduler
const OBJECT_TYPE_TO_SCHEDULER = {
  "4":  "material",
  "67": "stockTransfer",
};

// Rótulos amigáveis por tipo de scheduler
const SCHEDULER_TYPE_LABELS = {
  material:      "Material",
  stockTransfer: "Stock Transfer",
};

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "PENDING", label: "PENDENTE" },
  { value: "PROCESSED", label: "PROCESSADO" },
  { value: "ERROR", label: "ERRO" },
];

function formatDateTime(val) {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleString("pt-BR");
}

// Retorna label do intervalo para exibição no KPI
function schedulerIntervalLabel(cfg) {
  if (!cfg) return null;
  if (!cfg.enabled) return "Inativo";
  switch (cfg.mode) {
    case "interval":
      return cfg.intervalMinutes < 60
        ? `a cada ${cfg.intervalMinutes} min`
        : `a cada ${cfg.intervalMinutes / 60}h`;
    case "timesPerDay":
      return `${cfg.timesPerDay}x ao dia`;
    case "fixedTimes":
      return `${(cfg.times || []).length} horário(s)`;
    default:
      return "—";
  }
}

// Calcula próximo disparo em ms a partir de uma data base
function nextFireMs(cfg, baseMs) {
  if (!cfg || !cfg.enabled) return null;
  const now = Date.now();

  if (cfg.mode === "interval") {
    const intervalMs = (cfg.intervalMinutes || 1) * 60 * 1000;
    let next = baseMs + intervalMs;
    // avança até o futuro (caso tenha ficado parado)
    while (next < now) next += intervalMs;
    return next;
  }

  if (cfg.mode === "timesPerDay") {
    const times = cfg.timesPerDay || 1;
    const intervalMs = (24 * 60 * 60 * 1000) / times;
    let next = baseMs + intervalMs;
    while (next < now) next += intervalMs;
    return next;
  }

  if (cfg.mode === "fixedTimes") {
    const times = (cfg.times || []).sort();
    if (!times.length) return null;
    const nowDate = new Date();
    const candidates = [];
    // hoje e amanhã
    for (let dayOffset = 0; dayOffset <= 1; dayOffset++) {
      for (const t of times) {
        const [h, m] = t.split(":").map(Number);
        const d = new Date(nowDate);
        d.setDate(d.getDate() + dayOffset);
        d.setHours(h, m, 0, 0);
        if (d.getTime() > now) candidates.push(d.getTime());
      }
    }
    if (!candidates.length) return null;
    return Math.min(...candidates);
  }

  return null;
}

function calcNextExecution(item, schedulerTypeCfg) {
  if (!schedulerTypeCfg || !schedulerTypeCfg.enabled) return "—";
  if (item.Status === "PROCESSED") return "Concluído";

  const base = item.LastChangeAt;
  if (!base) return "Em breve";
  const baseMs = new Date(base).getTime();
  if (isNaN(baseMs)) return "—";

  const nextMs = nextFireMs(schedulerTypeCfg, baseMs);
  if (nextMs === null) return "—";

  const now = Date.now();
  if (nextMs <= now) return "Em breve";

  const diffSec = Math.round((nextMs - now) / 1000);
  if (diffSec < 60) return `em ${diffSec}s`;
  const diffMin = Math.round(diffSec / 60);
  const timeStr = new Date(nextMs).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `em ${diffMin} min (${timeStr})`;
}

function truncate(str, max = 60) {
  if (!str) return "—";
  return str.length > max ? str.slice(0, max) + "…" : str;
}

// ─── KPI CARD ────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <KpiBox color={color}>
      <KpiIcon color={color}><Icon /></KpiIcon>
      <KpiBody>
        <KpiValue>{value ?? "—"}</KpiValue>
        <KpiLabel>{label}</KpiLabel>
      </KpiBody>
    </KpiBox>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
function IFSQueuePage() {
  const history = useHistory();

  const [aplicacoes, setAplicacoes]     = useState([]);
  const [aplicacaoId, setAplicacaoId]   = useState("");
  const [schedulerConfig, setSchedulerConfig] = useState(null); // app.scheduler
  const [status, setStatus]           = useState("");
  const [objectType, setObjectType]   = useState("");
  const [entityKey, setEntityKey]     = useState("");
  const [uOs, setUOs]                 = useState("");
  const [uAtendRq, setUAtendRq]       = useState("");

  const [items, setItems]         = useState([]);
  const [kpis, setKpis]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize]   = useState(20);
  const [requeueing, setRequeueing]   = useState({});
  const [selected, setSelected]       = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [errorModal, setErrorModal]   = useState(null); // string ou null
  const [maximized, setMaximized]     = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(0); // 0 = desativado, valor em segundos
  const [countdown, setCountdown]     = useState(0);
  const timerRef                       = useRef(null);
  const countRef                       = useRef(null);

  // carrega aplicações e auto-seleciona a primeira
  useEffect(() => {
    fetchClient.get("/ifs/getAllAplicacao").then(({ data }) => {
      const list = (Array.isArray(data) ? data : data.items || []).filter(a => a.id && !a.id.includes("#"));
      setAplicacoes(list);
      if (list.length > 0) {
        const first = list[0];
        setAplicacaoId(first.id);
        setSchedulerConfig(first.scheduler || null);
        fetchQueue(1, first.id);
        fetchKpis(first.id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // KPIs — endpoint dedicado, só filtra por empresa (nunca pelos filtros da tela)
  const fetchKpis = useCallback(async (appId) => {
    if (!appId) return;
    try {
      const { data } = await fetchClient.get(`/ifs/kpi?aplicacaoId=${appId}`);
      setKpis({
        total:     data.total     ?? 0,
        pending:   data.pending   ?? 0,
        processed: data.processed ?? 0,
        errors:    data.error     ?? 0,
      });
    } catch { /* silencioso */ }
  }, []);

  const fetchQueue = useCallback(async (pg = 1, overrideAplicacaoId, overridePageSize) => {
    const appId = overrideAplicacaoId ?? aplicacaoId;
    if (!appId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ aplicacaoId: appId, page: pg, pageSize: overridePageSize ?? pageSize });
      if (status)     params.set("status",     status);
      if (objectType) params.set("objectType", objectType);
      if (entityKey)  params.set("entityKey",  entityKey.trim());
      if (uOs)        params.set("uOs",        uOs.trim());
      if (uAtendRq)   params.set("uAtendRq",   uAtendRq.trim());

      const { data } = await fetchClient.get(`/ifs/queue?${params}`);
      const list = data.items || [];
      setItems(list);
      setSelected(new Set());
      setTotalPages(data.totalPages || 1);
      setPage(pg);
    } catch {
      message.error("Erro ao carregar fila de integração");
    } finally {
      setLoading(false);
    }
  }, [aplicacaoId, status, objectType, entityKey, uOs, uAtendRq]);


  const handleSearch = (e) => {
    e.preventDefault();
    fetchQueue(1);
    fetchKpis(aplicacaoId);
  };

  const itemKey = (item) => `${item.ObjectType}||${item.EntityKey}`;

  const toggleSelect = (item) => {
    const k = itemKey(item);
    setSelected(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const requeueableItems = items.filter(i => i.Status !== "PENDING");
  const allSelected = requeueableItems.length > 0 && requeueableItems.every(i => selected.has(itemKey(i)));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(requeueableItems.map(itemKey)));
    }
  };

  const handleBulkRequeue = async () => {
    const toProcess = items.filter(i => selected.has(itemKey(i)));
    if (!toProcess.length) return;
    if (!window.confirm(`Deseja reenfileirar ${toProcess.length} item(s) para PENDING?`)) return;
    setBulkLoading(true);
    let ok = 0, fail = 0;
    await Promise.all(toProcess.map(async (item) => {
      try {
        await fetchClient.patch("/ifs/queue/requeue", {
          aplicacaoId,
          objectType: item.ObjectType,
          entityKey:  item.EntityKey,
        });
        ok++;
      } catch { fail++; }
    }));
    setBulkLoading(false);
    if (ok)   message.success(`${ok} item(s) reenfileirado(s) com sucesso`);
    if (fail) message.error(`${fail} item(s) falharam ao reenfileirar`);
    fetchQueue(page);
    fetchKpis(aplicacaoId);
  };

  const handleRequeue = async (item) => {
    if (!window.confirm(`Deseja reenfileirar o item "${item.EntityKey}" para PENDING?`)) return;
    const key = `${item.ObjectType}-${item.EntityKey}`;
    setRequeueing(r => ({ ...r, [key]: true }));
    try {
      await fetchClient.patch("/ifs/queue/requeue", {
        aplicacaoId,
        objectType: item.ObjectType,
        entityKey:  item.EntityKey,
      });
      message.success(`Item "${item.EntityKey}" reenfileirado com sucesso`);
      fetchQueue(page);
      fetchKpis(aplicacaoId);
    } catch (err) {
      message.error(err.response?.data?.message || "Erro ao reenfileirar item");
    } finally {
      setRequeueing(r => { const c = { ...r }; delete c[key]; return c; });
    }
  };

  // auto-refresh
  useEffect(() => {
    clearInterval(timerRef.current);
    clearInterval(countRef.current);
    if (!autoRefresh || !aplicacaoId) return;
    setCountdown(autoRefresh);
    timerRef.current = setInterval(() => {
      fetchQueue(page);
      fetchKpis(aplicacaoId);
      setCountdown(autoRefresh);
    }, autoRefresh * 1000);
    countRef.current = setInterval(() => {
      setCountdown(c => (c > 1 ? c - 1 : autoRefresh));
    }, 1000);
    return () => { clearInterval(timerRef.current); clearInterval(countRef.current); };
  }, [autoRefresh, aplicacaoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMaximize = () => {
    if (!maximized) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setMaximized(m => !m);
  };

  return (
    <Page $maximized={maximized}>
      <Header>
        <HeaderLeft>
          <BackBtn onClick={() => history.goBack()}>
            <ArrowBack style={{ fontSize: 20 }} />
          </BackBtn>
          <HeaderText>
            <HeaderTitle>Fila de Integração IFS</HeaderTitle>
            <HeaderSub>Monitoramento e reprocessamento de itens na fila</HeaderSub>
          </HeaderText>
        </HeaderLeft>
        <HeaderActions>
          <AutoRefreshWrap>
            <AutoRefreshLabel>Auto:</AutoRefreshLabel>
            <AutoRefreshSelect value={autoRefresh} onChange={e => setAutoRefresh(Number(e.target.value))}>
              <option value={0}>Desativado</option>
              <option value={30}>30s</option>
              <option value={60}>1 min</option>
              <option value={120}>2 min</option>
              <option value={300}>5 min</option>
              <option value={600}>10 min</option>
            </AutoRefreshSelect>
            {autoRefresh > 0 && <Countdown>{countdown}s</Countdown>}
          </AutoRefreshWrap>
          <RefreshBtn type="button" onClick={() => { fetchQueue(page); fetchKpis(aplicacaoId); }} disabled={loading || !aplicacaoId}>
            <Refresh style={{ fontSize: 18 }} />
            Atualizar
          </RefreshBtn>
          <MaxBtn type="button" onClick={toggleMaximize} title={maximized ? "Sair da tela cheia" : "Maximizar"}>
            {maximized ? <FullscreenExit style={{ fontSize: 20 }} /> : <Fullscreen style={{ fontSize: 20 }} />}
          </MaxBtn>
        </HeaderActions>
      </Header>

      {/* Filtros */}
      <FilterCard>
        <form onSubmit={handleSearch}>
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>Aplicação IFS *</FilterLabel>
              <FilterSelect value={aplicacaoId} onChange={e => {
                const id = e.target.value;
                setAplicacaoId(id);
                const app = aplicacoes.find(a => a.id === id);
                setSchedulerConfig(app?.scheduler || null);
                if (id) { fetchQueue(1, id); fetchKpis(id); }
              }}>
                <option value="">— Selecione a aplicação —</option>
                {aplicacoes.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Status</FilterLabel>
              <FilterSelect value={status} onChange={e => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Objeto SAP</FilterLabel>
              <FilterSelect value={objectType} onChange={e => setObjectType(e.target.value)}>
                {OBJECT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </FilterSelect>
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Código da Entidade</FilterLabel>
              <FilterInput
                placeholder="ex: A00001"
                value={entityKey}
                onChange={e => setEntityKey(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Nº OS</FilterLabel>
              <FilterInput
                placeholder="ex: OS-00456"
                value={uOs}
                onChange={e => setUOs(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Nº da Ordem</FilterLabel>
              <FilterInput
                placeholder="ex: 1234"
                value={uAtendRq}
                onChange={e => setUAtendRq(e.target.value)}
              />
            </FilterGroup>
            <SearchBtn type="submit" disabled={loading || !aplicacaoId}>Buscar</SearchBtn>
          </FilterGrid>
        </form>
      </FilterCard>

      {/* KPIs */}
      {kpis && (
        <KpiGrid>
          <KpiCard icon={ListAltOutlined}        label="Total de Registros" value={kpis.total}     color="#667eea" />
          <KpiCard icon={HourglassEmptyOutlined} label="Pendentes"     value={kpis.pending}   color="#f59e0b" />
          <KpiCard icon={CheckCircleOutline}     label="Processados"   value={kpis.processed} color="#10b981" />
          <KpiCard icon={ErrorOutline}           label="Com Erro"      value={kpis.errors}    color="#ef4444" />
          {schedulerConfig && Object.entries(SCHEDULER_TYPE_LABELS).map(([key, label]) => {
            const cfg = schedulerConfig[key];
            if (!cfg) return null;
            return (
              <KpiCard key={key} icon={Refresh}
                label={`Scheduler: ${label}`}
                value={schedulerIntervalLabel(cfg)}
                color={cfg.enabled ? "#0ea5e9" : "#94a3b8"}
              />
            );
          })}
        </KpiGrid>
      )}

      {/* Tabela */}
      <TableCard>
        {loading ? (
          <LoadingRow>Carregando…</LoadingRow>
        ) : !aplicacaoId ? (
          <EmptyRow>Selecione uma aplicação e clique em Buscar para carregar os dados.</EmptyRow>
        ) : items.length === 0 ? (
          <EmptyRow>Nenhum registro encontrado para os filtros selecionados.</EmptyRow>
        ) : (
          <>
            {selected.size > 0 && (
              <BulkBar>
                <BulkInfo><strong>{selected.size}</strong> item(s) selecionado(s)</BulkInfo>
                <BulkRequeueBtn type="button" onClick={handleBulkRequeue} disabled={bulkLoading}>
                  <ReplayOutlined style={{ fontSize: 16 }} />
                  {bulkLoading ? "Processando…" : `Reprocessar ${selected.size} item(s)`}
                </BulkRequeueBtn>
                <ClearSelBtn type="button" onClick={() => setSelected(new Set())}>Limpar seleção</ClearSelBtn>
              </BulkBar>
            )}
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th style={{ width: 40 }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll}
                        title="Selecionar todos reprocessáveis" />
                    </Th>
                    <Th>Objeto SAP</Th>
                    <Th>Código</Th>
                    <Th>Nº OS</Th>
                    <Th>Nº da Ordem</Th>
                    <Th>Status</Th>
                    <Th>Última Alteração</Th>
                    <Th>Processado em</Th>
                    <Th>Próxima Execução</Th>
                    <Th>Erro</Th>
                    <Th>Ação</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const key = `${item.ObjectType}-${item.EntityKey}-${i}`;
                    const k = itemKey(item);
                    const isLoading = requeueing[`${item.ObjectType}-${item.EntityKey}`];
                    const canSelect = item.Status !== "PENDING";
                    const isSelected = selected.has(k);
                    return (
                      <Tr key={key} $selected={isSelected}>
                        <Td>
                          {canSelect && (
                            <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(item)} />
                          )}
                        </Td>
                        <Td>{OBJECT_TYPE_LABELS[item.ObjectType] || item.ObjectType || "—"}</Td>
                        <Td><code>{item.EntityKey}</code></Td>
                        <Td>{item.U_OS || "—"}</Td>
                        <Td>{item.U_AtendRq || "—"}</Td>
                        <Td><StatusBadge status={item.Status}>{item.Status}</StatusBadge></Td>
                        <Td>{formatDateTime(item.LastChangeAt)}</Td>
                        <Td>{formatDateTime(item.ProcessedAt)}</Td>
                        <Td><NextExec status={item.Status}>{calcNextExecution(item, schedulerConfig?.[OBJECT_TYPE_TO_SCHEDULER[item.ObjectType]])}</NextExec></Td>
                        <Td>
                          {item.MessageError ? (
                            <ErrorCell onClick={() => setErrorModal(item.MessageError)} title="Clique para ver o erro completo">
                              {truncate(item.MessageError, 50)}
                            </ErrorCell>
                          ) : "—"}
                        </Td>
                        <Td>
                          {canSelect ? (
                            <RequeueBtn type="button" disabled={isLoading} onClick={() => handleRequeue(item)}>
                              <ReplayOutlined style={{ fontSize: 14 }} />
                              {isLoading ? "…" : "Reprocessar"}
                            </RequeueBtn>
                          ) : "—"}
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>

            <PaginationRow>
              <PagBtn type="button" onClick={() => fetchQueue(page - 1)} disabled={page <= 1 || loading}>‹ Anterior</PagBtn>
              <PagInfo>Página {page} de {totalPages}</PagInfo>
              <PagBtn type="button" onClick={() => fetchQueue(page + 1)} disabled={page >= totalPages || loading}>Próxima ›</PagBtn>
              <PageSizeSelect value={pageSize} onChange={e => {
                const s = Number(e.target.value);
                setPageSize(s);
                fetchQueue(1, undefined, s);
              }}>
                <option value={10}>10 / pág</option>
                <option value={20}>20 / pág</option>
                <option value={50}>50 / pág</option>
                <option value={100}>100 / pág</option>
              </PageSizeSelect>
            </PaginationRow>
          </>
        )}
      </TableCard>

      {/* Modal de Erro */}
      {errorModal && (
        <ErrorModalOverlay onClick={() => setErrorModal(null)}>
          <ErrorModalBox onClick={e => e.stopPropagation()}>
            <ErrorModalHeader>
              <ErrorModalTitle>Detalhe do Erro</ErrorModalTitle>
              <ErrorModalActions>
                <CopyBtn type="button" onClick={() => {
                  navigator.clipboard.writeText(errorModal);
                  message.success("Copiado!");
                }}>Copiar</CopyBtn>
                <CloseModalBtn type="button" onClick={() => setErrorModal(null)}>✕</CloseModalBtn>
              </ErrorModalActions>
            </ErrorModalHeader>
            <ErrorModalBody>{errorModal}</ErrorModalBody>
          </ErrorModalBox>
        </ErrorModalOverlay>
      )}
    </Page>
  );
}

export default IFSQueuePage;

// ─── STYLED ───────────────────────────────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: ${p => p.$maximized ? "16px" : "32px"};
  font-family: 'Mulish', sans-serif;
  ${p => p.$maximized && `
    position: fixed;
    inset: 0;
    z-index: 9999;
    overflow-y: auto;
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
`;
const HeaderLeft = styled.div`display:flex;align-items:center;gap:16px;`;
const BackBtn = styled.button`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  &:hover{background:#f1f5f9;}
`;
const HeaderText = styled.div``;
const HeaderTitle = styled.h1`margin:0;font-size:22px;font-weight:700;color:#1e293b;`;
const HeaderSub   = styled.p`margin:0;font-size:13px;color:#64748b;`;
const HeaderActions = styled.div`display:flex;align-items:center;gap:8px;`;

const AutoRefreshWrap = styled.div`
  display:flex;align-items:center;gap:6px;
  background:white;border:1px solid #e2e8f0;border-radius:10px;padding:6px 12px;
`;
const AutoRefreshLabel = styled.span`font-size:12px;font-weight:600;color:#64748b;white-space:nowrap;`;
const AutoRefreshSelect = styled.select`
  border:none;outline:none;font-size:13px;font-family:'Mulish',sans-serif;color:#334155;
  background:transparent;cursor:pointer;
`;
const Countdown = styled.span`
  font-size:12px;font-weight:700;color:#667eea;
  background:#eef2ff;border-radius:6px;padding:2px 8px;min-width:32px;text-align:center;
`;

const RefreshBtn = styled.button`
  display:flex;align-items:center;gap:6px;
  background:#667eea;color:white;border:none;border-radius:10px;
  padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer;
  &:hover:not(:disabled){background:#5a67d8;}
  &:disabled{opacity:.5;cursor:not-allowed;}
`;

const MaxBtn = styled.button`
  display:flex;align-items:center;justify-content:center;
  background:white;color:#475569;border:1px solid #e2e8f0;
  border-radius:10px;width:42px;height:42px;cursor:pointer;
  &:hover{background:#f1f5f9;border-color:#cbd5e1;}
`;

// KPIs
const KpiGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px;margin-bottom:24px;
`;
const KpiBox = styled.div`
  background:white;border-radius:14px;padding:20px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);
  border-left:4px solid ${p => p.color};
  display:flex;align-items:center;gap:14px;
  animation:${fadeIn} .4s ease;
`;
const KpiIcon = styled.div`
  background:${p => p.color}18;border-radius:10px;padding:10px;
  display:flex;align-items:center;justify-content:center;
  color:${p => p.color};
`;
const KpiBody  = styled.div``;
const KpiValue = styled.div`font-size:26px;font-weight:800;color:#1e293b;line-height:1;`;
const KpiLabel = styled.div`font-size:12px;color:#64748b;margin-top:4px;`;

// Filtros
const FilterCard = styled.div`
  background:white;border-radius:14px;padding:20px 24px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);margin-bottom:24px;
`;
const FilterGrid = styled.div`
  display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end;
`;
const FilterGroup = styled.div`display:flex;flex-direction:column;gap:4px;min-width:160px;flex:1;`;
const FilterLabel = styled.label`font-size:12px;font-weight:600;color:#64748b;`;
const FilterSelect = styled.select`
  border:1px solid #e2e8f0;border-radius:8px;padding:8px 10px;
  font-size:14px;font-family:'Mulish',sans-serif;color:#1e293b;
  &:focus{outline:none;border-color:#667eea;}
`;
const FilterInput = styled.input`
  border:1px solid #e2e8f0;border-radius:8px;padding:8px 10px;
  font-size:14px;font-family:'Mulish',sans-serif;color:#1e293b;
  &:focus{outline:none;border-color:#667eea;}
`;
const SearchBtn = styled.button`
  background:#667eea;color:white;border:none;border-radius:8px;
  padding:9px 24px;font-size:14px;font-weight:600;cursor:pointer;
  align-self:flex-end;white-space:nowrap;
  &:hover:not(:disabled){background:#5a67d8;}
  &:disabled{opacity:.5;cursor:not-allowed;}
`;

// Tabela
const TableCard = styled.div`
  background:white;border-radius:14px;
  box-shadow:0 2px 8px rgba(0,0,0,.06);overflow:hidden;
`;
const TableWrapper = styled.div`overflow-x:auto;`;
const Table = styled.table`width:100%;border-collapse:collapse;font-size:13px;`;
const Th = styled.th`
  padding:12px 16px;text-align:left;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.5px;color:#64748b;
  background:#f8fafc;border-bottom:1px solid #e2e8f0;white-space:nowrap;
`;
const Tr = styled.tr`
  border-bottom:1px solid #f1f5f9;
  background:${p => p.$selected ? "#eef2ff" : "transparent"};
  &:hover{background:${p => p.$selected ? "#e0e7ff" : "#fafbfc"};}
`;

const BulkBar = styled.div`
  display:flex;align-items:center;gap:12px;
  padding:10px 16px;background:#eef2ff;border-bottom:1px solid #c7d2fe;
`;
const BulkInfo = styled.span`font-size:13px;color:#3730a3;flex:1;`;
const BulkRequeueBtn = styled.button`
  display:inline-flex;align-items:center;gap:6px;
  background:#4f46e5;color:white;border:none;border-radius:8px;
  padding:7px 16px;font-size:13px;font-weight:600;cursor:pointer;
  &:hover:not(:disabled){background:#4338ca;}
  &:disabled{opacity:.6;cursor:not-allowed;}
`;
const ClearSelBtn = styled.button`
  background:transparent;border:1px solid #a5b4fc;border-radius:8px;
  padding:6px 12px;font-size:12px;color:#4f46e5;cursor:pointer;
  &:hover{background:#e0e7ff;}
`;
const Td = styled.td`padding:12px 16px;color:#334155;vertical-align:middle;max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;

const STATUS_COLORS = {
  PENDING:   { bg: "#fef3c7", color: "#d97706" },
  PROCESSED: { bg: "#d1fae5", color: "#065f46" },
  ERROR:     { bg: "#fee2e2", color: "#991b1b" },
};
const StatusBadge = styled.span`
  display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;
  background:${p => STATUS_COLORS[p.status]?.bg || "#f1f5f9"};
  color:${p => STATUS_COLORS[p.status]?.color || "#64748b"};
`;

const RequeueBtn = styled.button`
  display:inline-flex;align-items:center;gap:4px;
  background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;
  border-radius:6px;padding:5px 10px;font-size:12px;font-weight:600;cursor:pointer;
  &:hover:not(:disabled){background:#dcfce7;}
  &:disabled{opacity:.5;cursor:not-allowed;}
`;

const NextExec = styled.span`
  font-size:12px;font-weight:600;
  color:${p => p.status === "PROCESSED" ? "#10b981" : p.status === "ERROR" ? "#ef4444" : "#f59e0b"};
`;

const LoadingRow = styled.div`padding:48px;text-align:center;color:#64748b;`;
const EmptyRow   = styled.div`padding:48px;text-align:center;color:#94a3b8;font-size:14px;`;

const PaginationRow = styled.div`
  display:flex;align-items:center;justify-content:center;gap:16px;padding:16px;
  border-top:1px solid #f1f5f9;
`;
const PagBtn  = styled.button`
  background:white;border:1px solid #e2e8f0;border-radius:8px;
  padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;color:#334155;
  &:hover:not(:disabled){border-color:#667eea;color:#667eea;}
  &:disabled{opacity:.4;cursor:not-allowed;}
`;
const PagInfo = styled.span`font-size:13px;color:#64748b;`;
const PageSizeSelect = styled.select`
  margin-left:8px;padding:6px 10px;border:1px solid #e2e8f0;border-radius:8px;
  font-size:13px;color:#334155;background:white;cursor:pointer;
  &:focus{outline:none;border-color:#667eea;}
`;

const ErrorCell = styled.span`
  color:#ef4444;font-size:12px;cursor:pointer;text-decoration:underline dotted;
  &:hover{color:#b91c1c;}
`;

const ErrorModalOverlay = styled.div`
  position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:10000;
  display:flex;align-items:center;justify-content:center;padding:24px;
`;
const ErrorModalBox = styled.div`
  background:white;border-radius:12px;width:100%;max-width:700px;
  box-shadow:0 20px 60px rgba(0,0,0,.3);display:flex;flex-direction:column;max-height:80vh;
`;
const ErrorModalHeader = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:16px 20px;border-bottom:1px solid #f1f5f9;
`;
const ErrorModalTitle = styled.h3`margin:0;font-size:15px;font-weight:700;color:#1e293b;`;
const ErrorModalActions = styled.div`display:flex;gap:8px;align-items:center;`;
const CopyBtn = styled.button`
  background:#667eea;color:white;border:none;border-radius:6px;
  padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;
  &:hover{background:#5a6fd6;}
`;
const CloseModalBtn = styled.button`
  background:none;border:none;font-size:18px;cursor:pointer;color:#64748b;
  &:hover{color:#1e293b;}
`;
const ErrorModalBody = styled.pre`
  margin:0;padding:20px;font-size:13px;font-family:'Courier New',monospace;
  color:#1e293b;white-space:pre-wrap;word-break:break-all;overflow-y:auto;
  background:#f8fafc;border-radius:0 0 12px 12px;
`;
